/// Port of mobile/lib/settings/ui/settings_page.dart.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appEnv } from '@/app/env/appEnv';
import { LanguagePicker } from '@/settings/ui/LanguagePicker';
import { useCoarsePointer } from '@/app/input/pointerMode';
import { useAuthStore } from '@/auth/handler/authStore';
import * as devRepository from '@/settings/data/devRepository';
import type { AppTheme } from '@/settings/data/settingsLocalData';
import { useSettingsStore, useThemeColors } from '@/settings/handler/settingsStore';
import { KeyboardBindingsEditor } from '@/settings/ui/KeyboardBindingsEditor';
import { SupportSection } from '@/settings/ui/SupportSection';
import { TouchLayoutEditor } from '@/settings/ui/TouchLayoutEditor';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelThemePicker } from '@/ui/pixel/PixelThemePicker';
import { PixelToggle } from '@/ui/pixel/PixelToggle';
import { PixelVolumeSlider } from '@/ui/pixel/PixelVolumeSlider';

const sectionGap = 'mb-7';
const labelGap = 'mb-3';

export function SettingsPage() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const coarse = useCoarsePointer();
  const [scrolledUnder, setScrolledUnder] = useState(false);

  const theme = useSettingsStore((s) => s.theme);
  const musicEnabled = useSettingsStore((s) => s.musicEnabled);
  const sfxEnabled = useSettingsStore((s) => s.sfxEnabled);
  const musicVolume = useSettingsStore((s) => s.musicVolume);
  const sfxVolume = useSettingsStore((s) => s.sfxVolume);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const setMusicEnabled = useSettingsStore((s) => s.setMusicEnabled);
  const setSfxEnabled = useSettingsStore((s) => s.setSfxEnabled);
  const setMusicVolume = useSettingsStore((s) => s.setMusicVolume);
  const setSfxVolume = useSettingsStore((s) => s.setSfxVolume);

  const showDeveloper = appEnv.isDev || (appEnv.isSentryEnabled && !appEnv.isProd);

  return (
    <PageChrome title={t('settingsTitle')} scrolledUnder={scrolledUnder}>
      <div
        className="min-h-0 flex-1 overflow-y-auto px-6 pb-28 pt-5"
        onScroll={(event) => setScrolledUnder(event.currentTarget.scrollTop > 0.5)}
      >
        <Section title={t('settingsTheme')}>
          <PixelThemePicker
            value={theme}
            labelOf={(value: AppTheme) =>
              value === 'light' ? t('themeLight') : t('themeDark')
            }
            onChange={setTheme}
          />
        </Section>

        <Section title={t('settingsLanguage')}>
          <LanguagePicker />
        </Section>

        <Section title={t('controlsTitle')}>
          <PixelContainer
            radius={3}
            pixelScale={2}
            padding="16px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
          >
            {coarse ? <TouchLayoutEditor /> : <KeyboardBindingsEditor />}
          </PixelContainer>
        </Section>

        <Section title={t('settingsMusic')}>
          <PixelToggle value={musicEnabled} onChange={setMusicEnabled} />
          <div className="mt-2.5">
            <PixelVolumeSlider value={musicVolume} onChange={setMusicVolume} enabled={musicEnabled} />
          </div>
        </Section>

        <Section title={t('settingsSfx')}>
          <PixelToggle value={sfxEnabled} onChange={setSfxEnabled} />
          <div className="mt-2.5">
            <PixelVolumeSlider value={sfxVolume} onChange={setSfxVolume} enabled={sfxEnabled} />
          </div>
        </Section>

        <Section title={t('supportTitle')}>
          <SupportSection />
        </Section>

        <p className="text-center leading-none" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.55 }}>
          {t('settingsAppVersion', { version: appEnv.version, build: 'web' })}
        </p>

        {showDeveloper && (
          <Section title={t('settingsDeveloper')} className="mt-7">
            <DevToolsPanel />
          </Section>
        )}
      </div>
    </PageChrome>
  );
}

function DevToolsPanel() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const clearHighScore = useAuthStore((s) => s.clearHighScore);
  const signOut = useAuthStore((s) => s.signOut);
  const userEmail = useAuthStore((s) => s.user.email);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onDelete = async (): Promise<void> => {
    const trimmed = email.trim();
    if (!trimmed) {
      setMessage(t('settingsDeleteUserEmailRequired'));
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await devRepository.deleteUserByEmail(trimmed);
      if (userEmail.trim().toLowerCase() === trimmed.toLowerCase()) {
        await signOut();
      }
      setEmail('');
      setMessage(t('settingsDeleteUserSuccess'));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('settingsDeleteUserFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <PixelContainer
        radius={9}
        pixelScale={2}
        padding="12px 14px"
        backgroundColor={colors.surfaceContainerHigh}
        borderColor={colors.onSurface}
        onClick={() => void clearHighScore()}
      >
        <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('settingsClearHighScore')}
        </span>
      </PixelContainer>
      <p className="leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
        {t('settingsDeleteUserHint')}
      </p>
      <PixelContainer
        radius={9}
        pixelScale={2}
        padding="4px 12px"
        backgroundColor={colors.surfaceContainerHigh}
        borderColor={colors.onSurface}
      >
        <input
          type="email"
          value={email}
          disabled={busy}
          placeholder={t('authEmail')}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent outline-none"
          style={{ fontSize: 18, color: colors.onSurface, padding: '8px 0' }}
        />
      </PixelContainer>
      {message && (
        <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
          {message}
        </p>
      )}
      <PixelContainer
        radius={9}
        pixelScale={2}
        padding="12px 14px"
        backgroundColor={colors.surfaceContainerHigh}
        borderColor={colors.onSurface}
        onClick={busy ? undefined : () => void onDelete()}
      >
        <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface, opacity: busy ? 0.5 : 1 }}>
          {t('settingsDeleteUser')}
        </span>
      </PixelContainer>
    </div>
  );
}

function Section({
  title,
  children,
  className = sectionGap,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  const colors = useThemeColors();
  return (
    <section className={className}>
      <h2 className={`${labelGap} leading-none`} style={{ fontSize: 22, color: colors.onSurface }}>
        {title}
      </h2>
      {children}
    </section>
  );
}
